import React, { useEffect, useMemo, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { type EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import { groupAllText } from '@meitu/saas-editor-core/es/layers/group-layer/utils/layer';
import { SHORTCUT_FUNC_MAP, type ShortcutMenu } from '@meitu/saas-editor-core/es/plugins/shortcut-menu';
import { type StageToolbar } from '@meitu/saas-editor-core/es/plugins/stage-toolbar';
import {
    GroupRoleType,
    IGroupLayerProps,
    IImageLayerProps,
    ILayerProps,
    LayerType,
} from '@meitu/saas-editor-core/es/version-types';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { HeaderToolbarContext } from '../context';
import { ICallback } from '../types';
import Toolbar, { ToolBarType } from './com-toolbar/toolbar';
import ReplaceInspector from './replace-inspector';
import ThumbnailController from './thumbnail-controller';
import TitleController from './title-controller';
import './header-toolbar.less';

export type HeaderToolbarProps = React.ComponentProps<'div'> & {
    id: string;
    editor: Editor;
    isIntl?: boolean;
    events?: EventEmitter;
    inGroup?: boolean;
    BrandTextsPopup?: () => React.ReactNode;
    specifyToolBar?: string[];
    hideToolbar?: string[];
} & ICallback & {
        onMakeGroup?: () => void;
        onBrokeGroup?: () => void;
        onEditText?: () => void;
        onAI?: (func: string) => void;
    };

// 不需要复制按钮的图层类型
const noCopyBtnLayerTypes = [LayerType.WATERMARK];

type IActiveLayerProps = {
    layerType: LayerType;
    lock: boolean;
    textLayerId: string | null;
    thumbnail: string | null;
    groupAllText: boolean;
};

const resolveActiveLayerProps = (layer: ILayerProps): IActiveLayerProps => ({
    layerType: layer.layerType,
    lock: layer.lock,
    textLayerId: layer.layerType === LayerType.TEXT_LAYER ? layer.id : null,
    thumbnail: layer.layerType === LayerType.IMAGE_LAYER ? (layer.data as IImageLayerProps).url : null,
    groupAllText: groupAllText(layer) && layer.layerType === LayerType.GROUP_LAYER,
});

const Divider = () => {
    return <div className="c-header-toolbar-divider" />;
};

const HeaderToolbar: React.FC<HeaderToolbarProps> = (props) => {
    const {
        editor,
        inGroup,
        id,
        isIntl,
        events,
        style,
        BrandTextsPopup,
        onLock,
        onSort,
        onCopy,
        onDelete,
        beforeDelete,
        onCopyBrush,
        onApplyBrush,
        onMakeGroup,
        onBrokeGroup,
        onEditText,
        onAI,
        onCopyImageEffect,
        specifyToolBar = Object.values(ToolBarType),
    } = props;

    const [activeLayerProps, setActiveLayerProps] = useState<IActiveLayerProps | null>(() => {
        const layer = editor.services.flatLayer.getLayer(id);
        return layer ? resolveActiveLayerProps(layer) : null;
    });
    const [role, setRole] = useState<GroupRoleType | null>(null);
    const [hideSort, setHideSort] = useState<boolean>(
        !editor.getInstance<StageToolbar>('StageToolbar').getItemAvailability('hierarchy'),
    );

    function getActiveLayerProps(layer: ILayerProps | null, id: string) {
        const currentLayer = layer || editor.services.flatLayer.getLayer(id);
        if (!currentLayer) return;
        setActiveLayerProps(resolveActiveLayerProps(currentLayer));
    }

    useEffect(() => {
        const handler = editor.getInstance<ShortcutMenu>('ShortcutMenu').onClick((func) => {
            if (func === SHORTCUT_FUNC_MAP.delete) {
                onDelete?.();
            } else if (func === SHORTCUT_FUNC_MAP.createCopy) {
                onCopy?.();
            } else if (func === SHORTCUT_FUNC_MAP.makeGroup) {
                onMakeGroup?.();
            } else if (func === SHORTCUT_FUNC_MAP.brokeGroup) {
                onBrokeGroup?.();
            } else if (func === SHORTCUT_FUNC_MAP.editText) {
                onEditText?.();
            } else if (func === SHORTCUT_FUNC_MAP.textBrush) {
                onCopyBrush?.();
            } else if (func === SHORTCUT_FUNC_MAP.imageBrush) {
                onCopyImageEffect?.();
            } else if (
                func === SHORTCUT_FUNC_MAP.AI_TEXT ||
                func === SHORTCUT_FUNC_MAP.AI_MODIFY_IMAGE ||
                func === SHORTCUT_FUNC_MAP.AI_EXPEND_IMAGE ||
                func === SHORTCUT_FUNC_MAP.AI_GENERATE_IMAGE
            ) {
                onAI?.(func);
            }
        });
        return () => editor.off(handler);
    }, []);

    // 监听图层熟悉变化
    useEffect(() => {
        const handler = editor.services.flatLayer.onChange(() => {
            getActiveLayerProps(null, id);
        }, id);

        const layer = editor.services.flatLayer.getLayer(id);
        if (layer && layer.layerType === LayerType.GROUP_LAYER) {
            const extension = (layer.data as IGroupLayerProps).extension;
            if (extension?.role) {
                setRole(extension.role);
            } else setRole(null);
        } else {
            setRole(null);
        }

        return () => {
            editor.off(handler);
        };
    }, [id]);

    // 监听激活图层变化
    useEffect(() => {
        getActiveLayerProps(null, id);
        const handler = editor.services.frame.onActiveLayerDataUpdate((layer) => {
            setHideSort(!editor.getInstance<StageToolbar>('StageToolbar').getItemAvailability('hierarchy'));
            getActiveLayerProps(layer, id);
        });

        return () => {
            editor.off(handler);
        };
    }, [id]);

    const pickLayerProps = useListenActivePickLayerProps(
        editor,
        (layer) => {
            if (!layer) return null;
            return {
                pickLayerType: layer.layerType,
                parentId: layer.parentId,
                textLayerId: layer.layerType === LayerType.TEXT_LAYER ? layer.id : null,
                thumbnail: layer.layerType === LayerType.IMAGE_LAYER ? (layer.data as IImageLayerProps).url : null,
            };
        },
        false,
    );

    useEffect(() => {
        const toolbar = editor.getInstance<StageToolbar>('StageToolbar');
        setHideSort(!toolbar.getItemAvailability('hierarchy'));
        return toolbar.onAction((action) => {
            if (action.type === 'hierarchy-availability-change') {
                setHideSort(!action.available);
            }
        });
    }, []);

    const finalToolBar = useMemo(() => {
        return specifyToolBar.filter((type) => type !== ToolBarType.SORT || !hideSort);
    }, [specifyToolBar, hideSort]);

    //如果是组合的情况下，默认展示组合的组件。
    if (!inGroup && pickLayerProps?.parentId) return null;

    if (!inGroup && activeLayerProps?.groupAllText) return null;

    if (!activeLayerProps) return null;

    const { layerType, lock } = activeLayerProps;

    const hideCopyBtn = noCopyBtnLayerTypes.includes(layerType);
    const textLayerId = activeLayerProps.textLayerId || pickLayerProps?.textLayerId;

    const isImageLayer = pickLayerProps
        ? pickLayerProps.pickLayerType === LayerType.IMAGE_LAYER
        : activeLayerProps.layerType === LayerType.IMAGE_LAYER;

    const thumbnail = pickLayerProps?.thumbnail || activeLayerProps.thumbnail;

    // const role =

    const params = {
        id,
        textLayerId: textLayerId || null,
        lock,
        isIntl,
        layerType,
        thumbnail,
        pickLayerType: pickLayerProps?.pickLayerType || null,
        hideCopyBtn,
        events,
        role,
        onLock,
        onSort,
        onCopyBrush,
        onApplyBrush,
        onCopy,
        onDelete,
        beforeDelete,
        onCopyImageEffect,
    };

    return (
        <HeaderToolbarContext.Provider value={params}>
            <div className="c-header-toolbar" style={style}>
                <div className="c-header-toolbar-container">
                    <ThumbnailController />
                    <div className="c-header-toolbar-content">
                        <TitleController />
                        {BrandTextsPopup && BrandTextsPopup()}
                        <div className="c-header-toolbar-actions">
                            {isImageLayer ? <ReplaceInspector hideIcon={isIntl && finalToolBar?.length > 3} /> : null}
                            <Toolbar toolBar={finalToolBar} />
                        </div>
                    </div>
                </div>
                <Divider />
            </div>
        </HeaderToolbarContext.Provider>
    );
};

export default React.memo(HeaderToolbar);
